import React, { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, FlaskConical, ShieldAlert, BookOpen, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { fetchPeptides } from "../api";
import { PILLAR_PAGES, buildPillarJsonLd } from "../data/pillarPages";
import { getTierForPeptide } from "../data/peptideTiers";
import TierBadge from "../components/TierBadge";

// dbId -> slug (mirrors PeptideDetailPage)
const SLUG_MAP = {
  "1": "ghk-cu", "3": "nad", "4": "5-amino-1mq",
  "5": "bpc-157", "6": "tb-500", "8": "sermorelin",
  "9": "mots-c", "10": "pt-141", "11": "klotho",
};

// Section icons by index — keeps the JSX free of nested ternaries.
const SECTION_ICONS = [Sparkles, BookOpen, FlaskConical, ShieldAlert];
const getSectionIcon = (index) => SECTION_ICONS[index] || ShieldAlert;

const PillarPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const page = PILLAR_PAGES[slug];
  const [peptides, setPeptides] = useState([]);

  usePageSEO(page?.title, page?.description);

  useEffect(() => {
    let active = true;
    fetchPeptides()
      .then((data) => { if (active) setPeptides(data); })
      .catch(() => { /* ignore — page renders with empty list */ });
    return () => { active = false; };
  }, []);

  if (!page) return <Navigate to="/" replace />;

  const productsForPage = page.productSlugs
    .map((s) => {
      const id = Object.keys(SLUG_MAP).find((k) => SLUG_MAP[k] === s);
      const dbProduct = peptides.find((p) => p.id === id);
      if (!dbProduct) return null;
      return { ...dbProduct, slug: s };
    })
    .filter(Boolean);

  const jsonLd = buildPillarJsonLd(page, productsForPage);

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid={`pillar-${page.slug}`}>
      <Header />
      <JsonLd id={`pillar-jsonld-${page.slug}`} data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6" data-testid="pillar-breadcrumb">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">{page.breadcrumb}</span>
          </nav>
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">{page.eyebrow}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="pillar-h1">
            {page.h1}
          </h1>
          <p className="pillar-intro text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            {page.intro}
          </p>
        </div>
      </section>

      {/* Optional compliance callout — currently used to draw a hard wall
          between FDA-approved drug classes and research-only research peptides
          (e.g., GLP-1 RAs vs research weight-management peptides). */}
      {page.complianceCallout && (
        <section className="py-8 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="border-2 border-amber-300 bg-amber-50 rounded-2xl p-6 md:p-7" data-testid="pillar-compliance-callout">
              <h2 className="text-lg md:text-xl font-bold text-amber-900 mb-5 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-700" />
                {page.complianceCallout.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {page.complianceCallout.columns.map((col) => {
                  const isApproved = col.tone === "approved";
                  return (
                    <article
                      key={col.heading}
                      className={`border-2 ${isApproved ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"} rounded-xl p-5`}
                      data-testid={`callout-col-${col.tone}`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className={`text-xs font-bold uppercase tracking-wider ${isApproved ? "text-emerald-700" : "text-rose-700"}`}>
                          {col.eyebrow}
                        </p>
                        <TierBadge tier={isApproved ? 1 : 3} />
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-2">{col.heading}</h3>
                      <p className="text-sm text-[#5a4a40] leading-relaxed">{col.body}</p>
                    </article>
                  );
                })}
              </div>
              {page.complianceCallout.footer && (
                <p className="text-xs md:text-sm text-amber-900 mt-5 italic">{page.complianceCallout.footer}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sections */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6" data-testid="pillar-sections">
          {page.sections.map((s, i) => {
            const SectionIcon = getSectionIcon(i);
            return (
              <article
                key={s.heading}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`pillar-section-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                    <SectionIcon className="w-5 h-5 text-[#b89a6d]" />
                  </div>
                  <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{s.heading}</h2>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{s.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Products in this category */}
      {productsForPage.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-2">
              Research Catalog — {page.breadcrumb}
            </h2>
            <p className="text-[#7a6b62] mb-8">
              All products are research chemicals supplied for laboratory use only.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pillar-products">
              {productsForPage.map((p) => (
                <Link
                  key={p.slug}
                  to={`/peptides/${p.slug}`}
                  data-testid={`pillar-product-${p.slug}`}
                  className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-6 hover:border-[#d4a5ad] hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <span className="px-3 py-1 bg-[#f7e8e4] text-[#b89a6d] text-xs font-medium rounded-full">
                      {p.category}
                    </span>
                    <TierBadge tier={getTierForPeptide(p).tier} testid={`pillar-card-${p.id}`} />
                  </div>
                  <h3 className="text-lg font-bold text-[#3a2a26] mb-2 group-hover:text-[#b89a6d] transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-sm text-[#7a6b62] line-clamp-3 mb-4">{p.description}</p>
                  <span className="inline-flex items-center text-[#b89a6d] text-sm font-medium">
                    Research details <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Internal Links / Pillar Network */}
      {page.internalLinks?.length > 0 && (
        <section className="py-12 bg-[#fffaf5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-6">Continue your research</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="pillar-internal-links">
              {page.internalLinks.map((l) => {
                const linkClass = "px-4 py-3 bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg text-[#5a4a40] hover:text-[#b89a6d] text-sm font-medium transition-colors flex items-center justify-between group";
                const testId = `pillar-link-${l.to.replace(/[/:.?#&=]/g, "-")}`;
                if (l.external) {
                  return (
                    <a
                      key={l.to}
                      href={l.to}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid={testId}
                      className={linkClass}
                    >
                      <span>{l.label}</span>
                      <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </a>
                  );
                }
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={testId}
                    className={linkClass}
                  >
                    <span>{l.label}</span>
                    <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faqs?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-8 text-center">
              {page.breadcrumb} — Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-testid="pillar-faqs">
              {page.faqs.map((f, i) => (
                <details
                  key={f.q}
                  className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-5 group"
                  data-testid={`pillar-faq-${i}`}
                >
                  <summary className="cursor-pointer text-[#3a2a26] font-semibold list-none flex items-start justify-between gap-4">
                    <span>{f.q}</span>
                    <ChevronRight className="w-5 h-5 text-[#b89a6d] flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Compliance footer banner */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6 text-center">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong className="text-[#b89a6d]">Research-only language.</strong> Nothing on this page is medical advice.
              Products are research chemicals supplied for laboratory use only — not intended to diagnose, treat, cure,
              or prevent any disease. Discuss any aesthetic or wellness intervention with a licensed clinician.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PillarPage;
